import type { Paginated } from './types';

/**
 * The single HTTP seam. Three jobs:
 *   1. unwrap the `{ data, meta, error }` envelope (§6);
 *   2. turn an error envelope into a typed exception carrying the machine-readable
 *      `code`, so screens branch on codes rather than message strings;
 *   3. recover from an expired access token exactly once per request, transparently
 *      (FE-0-4).
 */

const BASE = '/api/v1';

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** True when the caller should send the user to the login page. */
  get isAuthError(): boolean {
    return (
      this.status === 401 ||
      this.code === 'UNAUTHENTICATED' ||
      this.code === 'SESSION_EXPIRED' ||
      this.code === 'SESSION_REUSE_DETECTED'
    );
  }
}

export class NetworkError extends Error {
  constructor(message = 'You appear to be offline.') {
    super(message);
    this.name = 'NetworkError';
  }
}

interface Envelope<T> {
  data: T | null;
  meta: (Record<string, unknown> & { total?: number; page?: number; pageSize?: number }) | null;
  error: { code: string; message: string; details?: unknown } | null;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
  /** Internal: prevents a refresh loop. */
  skipRefresh?: boolean;
  signal?: AbortSignal;
  /**
   * Sent as-is rather than JSON-encoded. Used by the content importer, where the
   * browser must set the multipart boundary itself — setting content-type manually
   * omits the boundary and the server cannot parse the upload.
   */
  formData?: FormData;
}

/**
 * A single in-flight refresh shared by every concurrent 401. Without this, a
 * dashboard firing four parallel requests on a stale token would attempt four
 * rotations — and rotation with reuse detection (§5.3) would read the extra ones as
 * a stolen token and kill the whole session.
 */
let refreshInFlight: Promise<boolean> | null = null;

async function refreshSession(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = fetch(`${BASE}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
    })
      .then((res) => res.ok)
      .catch(() => false)
      .finally(() => {
        // Cleared on the next tick so callers awaiting this promise all see the
        // same result before a new attempt can start.
        setTimeout(() => {
          refreshInFlight = null;
        }, 0);
      });
  }
  return refreshInFlight;
}

/** Listeners notified when the session is definitively gone, so the app can redirect. */
const sessionLostHandlers = new Set<() => void>();
export function onSessionLost(handler: () => void): () => void {
  sessionLostHandlers.add(handler);
  return () => sessionLostHandlers.delete(handler);
}

async function raw<T>(path: string, options: RequestOptions = {}): Promise<Envelope<T>> {
  const { method = 'GET', body, headers = {}, skipRefresh = false, signal, formData } = options;

  let response: Response;
  try {
    response = await fetch(`${BASE}${path}`, {
      method,
      // Cookies are the credential, so they must ride on every request.
      credentials: 'include',
      headers: {
        // Deliberately no content-type for FormData — the browser adds it with the
        // multipart boundary.
        ...(body !== undefined && !formData ? { 'content-type': 'application/json' } : {}),
        ...headers,
      },
      body: formData ?? (body !== undefined ? JSON.stringify(body) : undefined),
      signal,
    });
  } catch (cause) {
    if ((cause as Error).name === 'AbortError') throw cause;
    throw new NetworkError();
  }

  // 401 → rotate once, then replay. Anything else is surfaced as-is.
  if (response.status === 401 && !skipRefresh) {
    const refreshed = await refreshSession();
    if (refreshed) return raw<T>(path, { ...options, skipRefresh: true });

    for (const handler of sessionLostHandlers) handler();
  }

  let envelope: Envelope<T>;
  try {
    envelope = (await response.json()) as Envelope<T>;
  } catch {
    throw new ApiError(
      'INTERNAL',
      response.ok ? 'The server sent a malformed response.' : `Request failed (${response.status}).`,
      response.status,
    );
  }

  if (!response.ok || envelope.error) {
    throw new ApiError(
      envelope.error?.code ?? 'INTERNAL',
      envelope.error?.message ?? `Request failed (${response.status}).`,
      response.status,
      envelope.error?.details,
    );
  }

  return envelope;
}

/** Unwraps to `data`. Use for endpoints that do not paginate. */
export async function api<T>(path: string, options?: RequestOptions): Promise<T> {
  const envelope = await raw<T>(path, options);
  return envelope.data as T;
}

/** Unwraps to `{ items, total, page, pageSize }`, reading the pagination meta. */
export async function apiPaginated<T>(
  path: string,
  options?: RequestOptions,
): Promise<Paginated<T>> {
  const envelope = await raw<T[]>(path, options);
  return {
    items: envelope.data ?? [],
    total: Number(envelope.meta?.total ?? 0),
    page: Number(envelope.meta?.page ?? 1),
    pageSize: Number(envelope.meta?.pageSize ?? 20),
  };
}

/** Uploads a file as multipart/form-data and unwraps to `data`. */
export async function apiUpload<T>(path: string, file: File, field = 'file'): Promise<T> {
  const formData = new FormData();
  formData.append(field, file, file.name);

  const envelope = await raw<T>(path, { method: 'POST', formData });
  return envelope.data as T;
}

/** Generates the key for FE-3-23's double-submit guard. */
export function idempotencyKey(): string {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
